import * as React from 'react';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Empty from 'antd/lib/empty';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Layout from 'antd/lib/layout';
import Modal from 'antd/lib/modal';
import Popconfirm from 'antd/lib/popconfirm';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import {
    CheckCircleFilled,
    ClockCircleOutlined,
    ClockCircleFilled,
    CloseOutlined,
    CloseCircleFilled,
    EditOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as API from '@/api';
import {
    hotfixRequestErrorMessage,
    useApproveHotfixRequestMutation,
    useCancelHotfixRequestMutation,
    useGetHotfixRequestQuery,
    useRejectHotfixRequestMutation,
    useUpdateHotfixRequestMutation,
} from '@/middlewares/defaultApi';
import { useGetTimelineEventsHistoryQuery, type TimelineEventsAPIRequest } from '@/middlewares/paginationApi';
import { TimelineEventsFilterType } from '@/types/timelineEventsFilters';
import { EventsTimeline, ReleaseTimelineSectionContext } from '@/components/timelineEvents/EventsTimeline';
import {
    selectAppStoreType,
    selectAuthCredentials,
    selectUserCanPerformAction,
    selectDestinationInfoByDestinationStepConfigId,
    selectUser,
    selectUserById,
    selectOrgSettings,
} from '@/selectors/selectors';
import type { RootState } from '@/reducers/reducers';
import {
    AllRollbackResigningStrategies,
    ReleaseStepStatus,
    ReleaseType,
    RollbackResigningStrategy,
    type ReleaseTag,
} from '@/types';
import type { HotfixProposal, HotfixRequestApproval, HotfixRequest } from '@/types/hotfixRequests';
import type { AppComponentProps } from '@/typescriptHelpers';
import { displayStringForDestination, displayTextForResigningStrategy } from '@/helpers/displayHelpers';
import { appStoreSupportsRollbacks, getIsGooglePlayStore } from '@/helpers';
import { createAppRoute, createReleaseRoute } from '@/helpers/routeHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { ReleaseVersionFormItem } from '@/components/prepareRelease/ReleaseVersionFormItem';
import { ReleaseNameFormItem } from '@/components/prepareRelease/ReleaseNameFormItem';
import { MatchPhasedReleaseCheckbox } from '@/components/prepareRelease/MatchPhasedReleaseCheckbox';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { UserAvatarWithTooltip } from '@/components/triage/FixRequestModule';
import PermissionedActionButton, { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import { StepStatusAlert } from '@/components/StepStatusHeaderComponent';
import { antGrey, pageHeaderStyle, runwayColors, rwColors, titleStyle } from '@/components/styles/globalStyles';
import { validateProposalVersion } from './proposal';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import {
    canNavigateToApprovedRelease,
    buildHotfixApprovalPayload,
    hotfixRequestPollingInterval,
    hotfixRequestStatusLabel,
    isHotfixRequestConflict,
    proposalToReviewValues,
    reviewValuesToProposal,
    resultingReleaseRouteParam,
    type HotfixProposalReviewValues,
} from './reviewHelpers';

export interface HotfixRequestPageProps extends AppComponentProps {
    requestId: string;
}

const HotfixRequestPageLayout: React.FC<React.PropsWithChildren> = ({ children }) => (
    <Layout style={{ backgroundColor: runwayColors.white, position: 'relative' }}>
        <div style={{ padding: 24, paddingBottom: 95, overflow: 'auto' }}>
            <div style={{ ...pageHeaderStyle, paddingRight: 0, display: 'flex', alignItems: 'center' }}>
                <Text style={titleStyle} type="secondary">
                    Hotfix Request
                </Text>
            </div>
            <div style={{ width: '100%', maxWidth: 1160, paddingBlockStart: 20 }}>{children}</div>
        </div>
    </Layout>
);

const requestLabelStyle: React.CSSProperties = {
    color: antGrey.grey8,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
};
const requestActionButtonStyle: React.CSSProperties = {
    borderRadius: 4,
    fontWeight: 400,
    paddingInline: 16,
};

const requestStatusMeta = (status: HotfixRequest['status']) => {
    switch (status) {
        case 'approved':
            return {
                label: hotfixRequestStatusLabel(status),
                icon: <CheckCircleFilled style={{ color: rwColors.statusGreen, fontSize: 16 }} />,
            };
        case 'rejected':
            return {
                label: hotfixRequestStatusLabel(status),
                icon: <CloseCircleFilled style={{ color: rwColors.statusRed, fontSize: 16 }} />,
            };
        case 'cancelled':
            return {
                label: hotfixRequestStatusLabel(status),
                icon: <CloseCircleFilled style={{ color: antGrey.grey7, fontSize: 16 }} />,
            };
        case 'approving':
            return {
                label: hotfixRequestStatusLabel(status),
                icon: <LoadingOutlined spin style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
        case 'pending':
        default:
            return {
                label: hotfixRequestStatusLabel(status),
                icon: <ClockCircleFilled style={{ color: rwColors.statusYellow, fontSize: 16 }} />,
            };
    }
};

const proposalItems = (proposal: HotfixProposal) => [
    ['Type', proposal.releaseType === 'rollback' ? 'Rollback' : 'Roll-forward hotfix'],
    ['Base', proposal.base?.name || '--'],
    ['Rollback source', proposal.rollbackSourceVersion || '--'],
    ['Proposed version', proposal.proposedVersion],
    ['Release name', proposal.releaseName || '--'],
    ['Cherry-pick commits', proposal.commitsToCherryPick?.join(', ') || 'None'],
    ['Create branch', proposal.shouldCreateBranch ? 'Yes' : 'No'],
    ['Bump version', proposal.shouldBumpVersionOnBranch ? 'Yes' : 'No'],
    ['Match phased release', proposal.shouldMatchPreviousPhasedRelease ? 'Yes' : 'No'],
];

const RequestUserAvatar = ({ orgId, appId, userId }: { orgId: string; appId: string; userId: string }) => {
    const user = useSelector((state: RootState) => selectUserById(state, orgId, appId, userId));
    return <UserAvatarWithTooltip user={user} fallbackName={userId} size={20} />;
};

const AvatarPile = ({ orgId, appId, userIds }: { orgId: string; appId: string; userIds: string[] }) => (
    <div style={{ display: 'flex', paddingLeft: 4 }}>
        {userIds.map((userId, index) => (
            <div
                key={userId}
                style={{
                    marginLeft: index === 0 ? 0 : -8,
                    border: `2px solid ${antGrey.grey3}`,
                    borderRadius: '50%',
                    display: 'inline-flex',
                }}
            >
                <RequestUserAvatar orgId={orgId} appId={appId} userId={userId} />
            </div>
        ))}
    </div>
);

interface RequestFormResponse {
    label: string;
    value: string;
    isAutoGenerated?: boolean;
}

const ProposalDetailsGrid = ({ proposal }: { proposal: HotfixProposal }) => {
    const items = proposalItems(proposal);
    const columns = [items.slice(0, 5), items.slice(5)];

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                alignItems: 'start',
                backgroundColor: antGrey.grey3,
            }}
        >
            {columns.map((column, columnIndex) => (
                <div
                    key={columnIndex}
                    style={{ borderRight: columnIndex === 0 ? `1px solid ${antGrey.grey5}` : undefined }}
                >
                    {column.map(([label, value], index) => (
                        <div
                            key={label}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                                padding: '8px 12px',
                                backgroundColor: antGrey.grey2,
                                borderTop: `1px solid ${antGrey.grey5}`,
                                borderRight: columnIndex === 1 ? `1px solid ${antGrey.grey5}` : undefined,
                                borderBottom: index === column.length - 1 ? `1px solid ${antGrey.grey5}` : undefined,
                            }}
                        >
                            <Text style={{ color: antGrey.grey7 }}>{label}</Text>
                            <Text
                                style={{
                                    color: antGrey.grey8,
                                    fontWeight: label === 'Type' ? 600 : 400,
                                    fontFamily:
                                        (
                                            value !== '--' &&
                                            (label === 'Rollback source' || label === 'Proposed version')
                                        ) ?
                                            'monospace'
                                        :   undefined,
                                    textAlign: 'right',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {value}
                            </Text>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

const RequestFormResponses = ({ responses }: { responses: RequestFormResponse[] }) => {
    if (responses.length === 0) return null;
    const columnCount =
        responses.length === 1 ? 1
        : responses.length === 2 ? 2
        : 3;
    const gridTemplateColumns = (() => {
        if (responses.length === 1) return 'minmax(0, 1fr)';
        if (responses.length >= 3) return 'repeat(3, minmax(0, 1fr))';

        const contentWeights = responses.map((response) => Math.sqrt(Math.max(response.value.trim().length, 1)));
        const firstColumnShare = Math.min(
            2 / 3,
            Math.max(1 / 3, contentWeights[0] / (contentWeights[0] + contentWeights[1])),
        );
        return `minmax(0, ${firstColumnShare}fr) minmax(0, ${1 - firstColumnShare}fr)`;
    })();

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns,
                margin: '16px 8px',
            }}
        >
            {responses.map((response, index) => (
                <div
                    key={`${response.label}-${index}`}
                    style={{
                        minWidth: 0,
                        padding: '0 16px 4px',
                        borderLeft: index % columnCount === 0 ? undefined : `1px solid ${antGrey.grey5}`,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Text style={requestLabelStyle}>{response.label}</Text>
                        {response.isAutoGenerated && (
                            <Tooltip title="Automatically created by Runway when a hotfix branch was detected">
                                <InfoCircleOutlined style={{ color: antGrey.grey6, fontSize: 12 }} />
                            </Tooltip>
                        )}
                    </div>
                    <div
                        style={{
                            color: antGrey.grey8,
                            marginTop: 16,
                            overflowWrap: 'anywhere',
                            whiteSpace: 'pre-wrap',
                        }}
                    >
                        {response.value || '—'}
                    </div>
                </div>
            ))}
        </div>
    );
};

const RequestSummary = ({
    orgId,
    appId,
    request,
    approvals,
    requiredApprovalCount,
    formResponses,
    actions,
}: {
    orgId: string;
    appId: string;
    request: HotfixRequest;
    approvals: HotfixRequestApproval[];
    requiredApprovalCount: number;
    formResponses: RequestFormResponse[];
    actions?: React.ReactNode;
}) => {
    const status = requestStatusMeta(request.status);
    const effectiveProposal = request.finalProposal ?? request.approvalAttemptProposal ?? request.proposal;

    return (
        <div
            style={{
                backgroundColor: runwayColors.white,
                border: `1px solid ${antGrey.grey5}`,
                borderRadius: 8,
                overflow: 'hidden',
            }}
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    alignItems: 'center',
                    backgroundColor: antGrey.grey3,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px' }}>
                    {status.icon}
                    <Text style={requestLabelStyle}>{status.label}</Text>
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 10,
                        padding: '14px 16px',
                    }}
                >
                    <ClockCircleOutlined style={{ color: rwColors.statusYellow, fontSize: 13 }} />
                    <Text style={{ color: antGrey.grey8, fontSize: 13, whiteSpace: 'nowrap' }}>
                        {request.approvalCount} of {requiredApprovalCount} approvals received
                    </Text>
                    <AvatarPile orgId={orgId} appId={appId} userIds={approvals.map((approval) => approval.actorId)} />
                </div>
            </div>
            <div
                style={{
                    backgroundColor: antGrey.grey3,
                    borderTop: `1px solid ${antGrey.grey5}`,
                    padding: '16px',
                }}
            >
                <div
                    style={{
                        backgroundColor: runwayColors.white,
                        borderTop: `1px solid ${antGrey.grey5}`,
                        borderLeft: `1px solid ${antGrey.grey5}`,
                        borderRadius: 4,
                        overflow: 'hidden',
                    }}
                >
                    <div style={{ display: 'flow-root', borderRight: `1px solid ${antGrey.grey5}` }}>
                        <RequestFormResponses responses={formResponses} />
                    </div>
                    <ProposalDetailsGrid proposal={effectiveProposal} />
                </div>
            </div>
            {actions && <div style={{ backgroundColor: antGrey.grey3, padding: '0 16px 16px' }}>{actions}</div>}
        </div>
    );
};

export default function HotfixRequestPage({ orgId, appId, requestId }: HotfixRequestPageProps) {
    const history = useHistory();
    const user = useSelector(selectUser);
    const canManageRequest = useSelector((state: RootState) =>
        selectUserCanPerformAction(
            state,
            RunwayUserAction.UACreateHotfix,
            undefined,
            undefined,
            undefined,
            false,
            appId,
        ),
    );
    const orgSettings = useSelector((state: RootState) => selectOrgSettings(state, orgId));
    const auth = useSelector(selectAuthCredentials);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const destinationsById = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );
    const destinations = React.useMemo(
        () =>
            Object.entries(destinationsById ?? {}).map(([stepConfigId, destination]) => ({
                ...destination,
                stepConfigId: destination.stepConfigId || stepConfigId,
            })),
        [destinationsById],
    );
    const scope = { orgId, appId, requestId };
    const [pollingInterval, setPollingInterval] = React.useState(0);
    const query = useGetHotfixRequestQuery(scope, {
        pollingInterval,
    });
    const detail = query.currentData;
    const request = detail?.request;
    const timelineHistoryRequest: TimelineEventsAPIRequest = React.useMemo(
        () => ({
            orgId,
            appId,
            releaseId: '',
            releaseStepType: '' as TimelineEventsAPIRequest['releaseStepType'],
            workflowId: '',
            workflowRunId: '',
            timelineEventFilters: [
                { type: TimelineEventsFilterType.HotfixRequest as const, hotfixRequestId: requestId },
            ],
        }),
        [orgId, appId, requestId],
    );
    const timelineQuery = useGetTimelineEventsHistoryQuery(timelineHistoryRequest);
    const timelineEvents = timelineQuery.currentData?.data?.events ?? [];
    const [approve, approveMutation] = useApproveHotfixRequestMutation();
    const [reject, rejectMutation] = useRejectHotfixRequestMutation();
    const [cancel, cancelMutation] = useCancelHotfixRequestMutation();
    const [updateRequest, updateMutation] = useUpdateHotfixRequestMutation();
    const [form] = Form.useForm<HotfixProposalReviewValues>();
    const releaseType = Form.useWatch('releaseType', form);
    const allDestinations = Form.useWatch('allDestinations', form);
    const shouldMatchPreviousPhasedRelease = Form.useWatch('shouldMatchPreviousPhasedRelease', form) ?? false;
    const [actionReason, setActionReason] = React.useState('');
    const [actionError, setActionError] = React.useState('');
    const [conflictMessage, setConflictMessage] = React.useState('');
    const [reviewModalOpen, setReviewModalOpen] = React.useState(false);
    const [editingRequest, setEditingRequest] = React.useState(false);
    const [rejectModalOpen, setRejectModalOpen] = React.useState(false);
    const [bases, setBases] = React.useState<ReleaseTag[]>([]);
    const [sources, setSources] = React.useState<string[]>([]);
    const seededRequestId = React.useRef<string>();
    const sourceProposal = request?.proposal;
    const approvals = detail?.approvals ?? [];
    const policy = request?.policySnapshot;
    const hasApproverAllowlist =
        (policy?.requiredApproverUserIds.length ?? 0) > 0 || (policy?.requiredApproverUserGroupIds.length ?? 0) > 0;
    const isAllowlisted =
        !hasApproverAllowlist ||
        (policy?.requiredApproverUserIds.includes(user.id) ?? false) ||
        (policy?.requiredApproverUserGroupIds.some((groupId) =>
            orgSettings?.groups?.[groupId]?.members?.includes(user.id),
        ) ??
            false);
    const canApprove = !!canManageRequest && isAllowlisted;
    const canCancel = request?.createdBy === user.id || canApprove;
    const canEdit = request?.createdBy === user.id;
    const mutating =
        approveMutation.isLoading || rejectMutation.isLoading || cancelMutation.isLoading || updateMutation.isLoading;

    React.useEffect(() => {
        document.title = 'Hotfix request';
    }, []);

    React.useEffect(() => {
        setPollingInterval(hotfixRequestPollingInterval(request?.status));
    }, [request?.status]);

    React.useEffect(() => {
        if (!request || !sourceProposal || seededRequestId.current === requestId) return;
        form.resetFields();
        form.setFieldsValue(proposalToReviewValues(sourceProposal));
        seededRequestId.current = requestId;
        // Seed once per request. Refetches, including conflicts, intentionally preserve the draft.
    }, [request, requestId, sourceProposal, form]);

    React.useEffect(() => {
        if ((!canApprove && !canEdit) || request?.status !== 'pending') return undefined;
        let cancelled = false;
        Promise.all([
            API.getPossibleBaseTags({ orgId, appId }, auth).catch(() => []),
            appStoreSupportsRollbacks(appStoreType) ?
                API.getPossibleSourceVersions({ orgId, appId }, auth).catch(() => [])
            :   Promise.resolve([]),
        ]).then(([loadedBases, loadedSources]) => {
            if (!cancelled) {
                setBases(loadedBases ?? []);
                setSources(loadedSources ?? []);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [canApprove, canEdit, request?.status, orgId, appId, auth, appStoreType]);

    const handleMutationError = async (error: unknown) => {
        setActionError(hotfixRequestErrorMessage(error));
        if (isHotfixRequestConflict(error)) {
            setConflictMessage('This request changed. Review the latest state, then explicitly try again.');
        }
        await query.refetch();
    };

    const clearMutationErrors = () => {
        setActionError('');
        setConflictMessage('');
    };

    const approveRequest = async (values: HotfixProposalReviewValues) => {
        if (!request || !sourceProposal || request.status !== 'pending') return;
        clearMutationErrors();
        try {
            const approvalPayload = buildHotfixApprovalPayload(request, values, sourceProposal, bases);
            const proposalChanged = JSON.stringify(request.proposal) !== JSON.stringify(approvalPayload.finalProposal);
            const updatedRequest = await approve({
                ...scope,
                ...approvalPayload,
            }).unwrap();
            globalMessage.success(
                updatedRequest.request.status === 'approved' || updatedRequest.request.status === 'approving' ?
                    'Final approval recorded. The release is being created.'
                : proposalChanged ? 'Proposal updated. Previous approvals were reset, and your approval was recorded.'
                : 'Approval recorded.',
            );
            setReviewModalOpen(false);
            if (updatedRequest.request.status === 'approved') {
                history.replace(createAppRoute(orgId, appId));
            }
        } catch (error) {
            await handleMutationError(error);
        }
    };

    const editRequest = async (values: HotfixProposalReviewValues) => {
        if (!request || request.status !== 'pending' || !canEdit) return;
        clearMutationErrors();
        try {
            await updateRequest({
                ...scope,
                expectedRevision: request.revision,
                proposal: reviewValuesToProposal(values, request.proposal, bases),
                justification: values.formFields?.justification?.trim() ?? '',
                formFieldValues: policy?.formFieldsSettings.map((field) => {
                    const fieldId = field.name.trim().toLowerCase().replace(/\s+/g, '-');
                    return { fieldId, value: values.formFields?.[fieldId] ?? '' };
                }),
            }).unwrap();
            globalMessage.success('Hotfix request updated. Previous approvals were reset.');
            setReviewModalOpen(false);
            setEditingRequest(false);
        } catch (error) {
            await handleMutationError(error);
        }
    };

    const openEditModal = () => {
        if (!request) return;
        clearMutationErrors();
        const formFields = (request.formFieldValues ?? []).reduce<Record<string, string>>((values, answer) => {
            values[answer.fieldId] = answer.value;
            return values;
        }, {});
        if (request.justification && !formFields.justification) formFields.justification = request.justification;
        form.setFieldsValue({ ...proposalToReviewValues(request.proposal), formFields });
        setEditingRequest(true);
        setReviewModalOpen(true);
    };

    const openReviewModal = () => {
        if (!sourceProposal) return;
        form.setFieldsValue(proposalToReviewValues(sourceProposal));
        setEditingRequest(false);
        setReviewModalOpen(true);
    };

    const transitionRequest = async (kind: 'reject' | 'cancel') => {
        if (!request || request.status !== 'pending' || (kind === 'reject' && !actionReason.trim())) return;
        clearMutationErrors();
        try {
            const updatedRequest = await (
                kind === 'reject' ?
                    reject({ ...scope, reason: actionReason.trim() })
                :   cancel(scope)).unwrap();
            globalMessage.success(`Hotfix request ${kind === 'reject' ? 'rejected' : 'cancelled'}.`);
            setRejectModalOpen(false);
            if (updatedRequest.request.status === 'rejected' || updatedRequest.request.status === 'cancelled') {
                history.replace(createAppRoute(orgId, appId));
            }
        } catch (error) {
            await handleMutationError(error);
        }
    };

    const openRejectModal = () => {
        clearMutationErrors();
        setActionReason('');
        setRejectModalOpen(true);
    };

    if (query.isLoading && !detail) {
        return (
            <HotfixRequestPageLayout>
                <Spin />
            </HotfixRequestPageLayout>
        );
    }
    if (query.error && !detail) {
        return (
            <HotfixRequestPageLayout>
                <Alert
                    type="error"
                    message={hotfixRequestErrorMessage(query.error)}
                    action={<Button onClick={query.refetch}>Retry</Button>}
                />
            </HotfixRequestPageLayout>
        );
    }
    if (!request || !detail || !sourceProposal) {
        return (
            <HotfixRequestPageLayout>
                <Empty description="Hotfix request not found" />
            </HotfixRequestPageLayout>
        );
    }
    if (!policy) {
        return (
            <HotfixRequestPageLayout>
                <Alert type="error" message="Hotfix request policy snapshot is missing" />
            </HotfixRequestPageLayout>
        );
    }

    const successfulApproval = canNavigateToApprovedRelease(request);
    const isAutoGenerated = request.origin === 'branchDetection';
    const formResponses: RequestFormResponse[] =
        request.formFieldValues?.length ?
            request.formFieldValues.map((answer) => ({
                label:
                    policy.formFieldsSettings.find(
                        (field) => field.name.trim().toLowerCase().replace(/\s+/g, '-') === answer.fieldId,
                    )?.name ?? answer.fieldId,
                value: answer.value,
                isAutoGenerated,
            }))
        :   [{ label: 'Justification', value: request.justification, isAutoGenerated }];
    const requestActions =
        (canApprove || canCancel || canEdit) && request.status === 'pending' ?
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {canApprove && (
                    <>
                        <Button
                            type="primary"
                            size="large"
                            icon={<SearchOutlined />}
                            style={requestActionButtonStyle}
                            onClick={openReviewModal}
                        >
                            Review hotfix request
                        </Button>
                        <Button
                            danger
                            size="large"
                            icon={<CloseOutlined />}
                            style={requestActionButtonStyle}
                            onClick={openRejectModal}
                        >
                            Reject
                        </Button>
                    </>
                )}
                {canCancel && (
                    <Popconfirm
                        title="Are you sure you want to cancel this hotfix request?"
                        okText="Yes, cancel"
                        cancelText="Cancel"
                        placement="top"
                        disabled={mutating}
                        okButtonProps={{ loading: cancelMutation.isLoading }}
                        onConfirm={() => transitionRequest('cancel')}
                    >
                        <Button size="large" style={requestActionButtonStyle} disabled={mutating}>
                            Cancel request
                        </Button>
                    </Popconfirm>
                )}
                {canEdit && (
                    <Tooltip title="Edit request">
                        <Button
                            type="text"
                            size="large"
                            aria-label="Edit request"
                            icon={<EditOutlined />}
                            disabled={mutating}
                            onClick={openEditModal}
                            style={{ marginLeft: 'auto', color: rwColors.primary }}
                        />
                    </Tooltip>
                )}
            </div>
        :   undefined;
    const hasRequestBanner =
        request.status === 'pending' ||
        request.status === 'approving' ||
        request.status === 'rejected' ||
        request.status === 'cancelled' ||
        successfulApproval ||
        !!request.lastApprovalError ||
        !!conflictMessage ||
        !!actionError;

    return (
        <HotfixRequestPageLayout>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {hasRequestBanner && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {request.status === 'pending' && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Pending}
                                statusReasonString={`This hotfix request still needs ${Math.max(
                                    policy.requiredApprovalCount - request.approvalCount,
                                    0,
                                )} more ${policy.requiredApprovalCount - request.approvalCount === 1 ? 'approval' : 'approvals'}.`}
                                style={{ width: '100%' }}
                            />
                        )}
                        {request.status === 'approving' && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Pending}
                                statusReasonString="This hotfix request has been approved and is temporarily frozen while the hotfix release is being created."
                                style={{ width: '100%' }}
                            />
                        )}
                        {request.status === 'rejected' && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Blocked}
                                statusReasonString={`This hotfix request was rejected, with reason:${
                                    request.terminalReason ? ` ${request.terminalReason}` : ''
                                }`}
                                style={{ width: '100%' }}
                            />
                        )}
                        {request.status === 'cancelled' && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Inactive}
                                statusReasonString={`This hotfix request was cancelled.${
                                    request.terminalReason ? ` ${request.terminalReason}` : ''
                                }`}
                                style={{ width: '100%' }}
                            />
                        )}
                        {successfulApproval && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Ready}
                                statusReasonString="The hotfix request was approved, and the hotfix release has been created."
                                style={{ width: '100%' }}
                                action={
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            history.push(
                                                createReleaseRoute(
                                                    orgId,
                                                    appId,
                                                    resultingReleaseRouteParam(appId, request.resultingReleaseId!),
                                                ),
                                            )
                                        }
                                    >
                                        View release
                                    </Button>
                                }
                            />
                        )}
                        {request.lastApprovalError && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Blocked}
                                statusReasonString={`Approval failed. ${request.lastApprovalError}`}
                                style={{ width: '100%' }}
                            />
                        )}
                        {conflictMessage && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Pending}
                                statusReasonString={conflictMessage}
                                style={{ width: '100%' }}
                            />
                        )}
                        {actionError && (
                            <StepStatusAlert
                                status={ReleaseStepStatus.Blocked}
                                statusReasonString={actionError}
                                style={{ width: '100%' }}
                            />
                        )}
                    </div>
                )}
                <RequestSummary
                    orgId={orgId}
                    appId={appId}
                    request={request}
                    approvals={approvals}
                    requiredApprovalCount={policy.requiredApprovalCount}
                    formResponses={formResponses}
                    actions={requestActions}
                />

                {(canApprove || canCancel || canEdit) && request.status === 'pending' && (
                    <>
                        <Modal
                            title={editingRequest ? 'Edit hotfix request' : 'Review hotfix request'}
                            open={reviewModalOpen}
                            onCancel={() => setReviewModalOpen(false)}
                            footer={null}
                            width={680}
                        >
                            <Form
                                form={form}
                                layout="vertical"
                                disabled={mutating}
                                onFinish={editingRequest ? editRequest : approveRequest}
                            >
                                <Alert
                                    type="info"
                                    message={
                                        editingRequest ?
                                            'Changes reset all existing approvals and require the request to be approved again.'
                                        :   'If you change the proposal, existing approvals will be reset before your approval is recorded.'
                                    }
                                    style={{ marginBottom: 16 }}
                                />
                                {editingRequest &&
                                    policy.formFieldsSettings.map((field) => {
                                        const fieldId = field.name.trim().toLowerCase().replace(/\s+/g, '-');
                                        return (
                                            <Form.Item
                                                key={fieldId}
                                                name={['formFields', fieldId]}
                                                label={field.name}
                                                extra={field.description}
                                                rules={[
                                                    { required: field.required },
                                                    ...(field.minCharacters ? [{ min: field.minCharacters }] : []),
                                                ]}
                                            >
                                                <Input.TextArea
                                                    autoSize={{ minRows: 3, maxRows: 8 }}
                                                    placeholder={field.placeholder}
                                                />
                                            </Form.Item>
                                        );
                                    })}
                                <ReleaseVersionFormItem
                                    disabled={mutating}
                                    labelCol={{ flex: 'initial' }}
                                    onValidate={validateProposalVersion}
                                />
                                <ReleaseNameFormItem disabled={mutating} labelCol={{ flex: 'initial' }} />
                                <Form.Item name="releaseType" label="Request type">
                                    <Radio.Group>
                                        <Radio value="hotfix">Roll-forward hotfix</Radio>
                                        {appStoreSupportsRollbacks(appStoreType) && (
                                            <Radio value="rollback">Rollback</Radio>
                                        )}
                                    </Radio.Group>
                                </Form.Item>
                                {releaseType === 'rollback' ?
                                    <>
                                        <Form.Item
                                            name="sourceVersion"
                                            label="Source version"
                                            rules={[{ required: true }]}
                                        >
                                            <Select
                                                showSearch
                                                options={sources.map((value) => ({ value, label: value }))}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            name="resigningStrategy"
                                            label="Resigning strategy"
                                            rules={[{ required: true }]}
                                        >
                                            <Select
                                                options={AllRollbackResigningStrategies.map(
                                                    (value: RollbackResigningStrategy) => ({
                                                        value,
                                                        label: displayTextForResigningStrategy(value),
                                                    }),
                                                )}
                                            />
                                        </Form.Item>
                                    </>
                                :   <>
                                        <Form.Item name="baseForHotfix" label="Base">
                                            <Select
                                                allowClear
                                                showSearch
                                                options={bases.map((base) => ({ value: base.name, label: base.name }))}
                                            />
                                        </Form.Item>
                                        <Form.Item name="shouldCreateBranch" valuePropName="checked">
                                            <Checkbox>Create a new branch</Checkbox>
                                        </Form.Item>
                                        <Form.Item name="shouldBumpVersionOnBranch" valuePropName="checked">
                                            <Checkbox>Bump version</Checkbox>
                                        </Form.Item>
                                        <Form.Item name="commitHashes" label="Commits to cherry-pick">
                                            <Select mode="tags" tokenSeparators={[',', ' ']} />
                                        </Form.Item>
                                        {getIsGooglePlayStore(appStoreType) && (
                                            <div style={{ marginBottom: 16 }}>
                                                <MatchPhasedReleaseCheckbox
                                                    visible
                                                    checked={shouldMatchPreviousPhasedRelease}
                                                    disabled={mutating}
                                                    onChange={(checked) =>
                                                        form.setFieldValue('shouldMatchPreviousPhasedRelease', checked)
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                }
                                {destinations.length > 1 && (
                                    <>
                                        <Form.Item name="allDestinations" valuePropName="checked">
                                            <Checkbox>All destinations</Checkbox>
                                        </Form.Item>
                                        {!allDestinations && (
                                            <Space align="start" style={{ width: '100%' }}>
                                                <Form.Item name="destinationAction">
                                                    <Select
                                                        style={{ width: 120 }}
                                                        options={[
                                                            { value: 'include', label: 'Include' },
                                                            { value: 'exclude', label: 'Exclude' },
                                                        ]}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    name="destinationIds"
                                                    label="Destinations"
                                                    rules={[{ required: true, type: 'array', min: 1 }]}
                                                >
                                                    <Select
                                                        mode="multiple"
                                                        style={{ minWidth: 320 }}
                                                        placeholder="Select destinations"
                                                        optionLabelProp="label"
                                                        maxTagCount="responsive"
                                                    >
                                                        {destinations.map((destination) => {
                                                            const displayName = displayStringForDestination(
                                                                destination.platform,
                                                                destination.appStoreType,
                                                                destination.name,
                                                                destination.appName,
                                                            );
                                                            return (
                                                                <Select.Option
                                                                    key={destination.stepConfigId}
                                                                    value={destination.stepConfigId}
                                                                    label={displayName}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 8,
                                                                        }}
                                                                    >
                                                                        <DestinationCounter
                                                                            destinations={[destination]}
                                                                            size={20}
                                                                        />
                                                                        <span>{displayName}</span>
                                                                    </div>
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </Form.Item>
                                            </Space>
                                        )}
                                    </>
                                )}
                                {editingRequest ?
                                    <Button type="primary" htmlType="submit" loading={updateMutation.isLoading}>
                                        Save changes
                                    </Button>
                                :   <Tooltip title={isAllowlisted ? undefined : DISALLOWED_ACTION_ERROR_MESSAGE}>
                                        <span style={{ display: 'inline-block' }}>
                                            <PermissionedActionButton
                                                actionType={RunwayUserAction.UACreateHotfix}
                                                appId={appId}
                                                type="primary"
                                                htmlType="submit"
                                                loading={approveMutation.isLoading}
                                                disabled={mutating || !isAllowlisted}
                                            >
                                                {request.approvalCount + 1 >= policy.requiredApprovalCount ?
                                                    'Approve and create release'
                                                :   'Approve hotfix request'}
                                            </PermissionedActionButton>
                                        </span>
                                    </Tooltip>
                                }
                            </Form>
                        </Modal>
                        <Modal
                            title="Reject hotfix request"
                            open={rejectModalOpen}
                            onCancel={() => setRejectModalOpen(false)}
                            footer={null}
                        >
                            <Input.TextArea
                                value={actionReason}
                                onChange={(event) => setActionReason(event.target.value)}
                                disabled={mutating}
                                rows={3}
                                placeholder="Reason required for rejection"
                            />
                            <Button
                                danger
                                type="primary"
                                style={{ marginTop: 12 }}
                                disabled={mutating || !actionReason.trim()}
                                loading={rejectMutation.isLoading}
                                onClick={() => transitionRequest('reject')}
                            >
                                Reject
                            </Button>
                        </Modal>
                    </>
                )}

                <section style={{ marginTop: 32 }}>
                    <Text style={{ color: antGrey.grey8, fontSize: 16, fontWeight: 500 }}>Timeline</Text>
                    <div style={{ borderTop: `1px dashed ${antGrey.grey6}`, marginTop: 8, paddingTop: 12 }}>
                        <EventsTimeline
                            events={timelineEvents}
                            isLoading={timelineQuery.isFetching && timelineEvents.length === 0}
                            orgId={orgId}
                            appId={appId}
                            releaseType={
                                sourceProposal.releaseType === 'rollback' ? ReleaseType.Rollback : ReleaseType.Hotfix
                            }
                            context={ReleaseTimelineSectionContext.AppTimeline}
                            shouldHideUpcomingEvents
                        />
                        {!timelineQuery.isFetching && timelineEvents.length === 0 && (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No history recorded" />
                        )}
                    </div>
                </section>
            </Space>
        </HotfixRequestPageLayout>
    );
}
